// FILE: ProjectFocusSwitcher.tsx
// Purpose: Switches the project shown by the focused sidebar, or returns to all projects.
// Exports: ProjectFocusSwitcher

import type { Project } from "../../types";
import type { CrossProjectAttention } from "../Sidebar.focus.logic";
import { ChevronDownIcon } from "~/lib/icons";
import { resolveProjectColor } from "~/lib/projectIdentity";
import { ProjectAvatar } from "../ProjectAvatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/menu";

export function ProjectFocusSwitcher(props: {
  focusedProject: Project;
  projects: readonly Project[];
  attention: CrossProjectAttention;
  onSelectProject: (project: Project) => void;
  onShowAllProjects: () => void;
}) {
  const attentionByProjectId = new Map(
    props.attention.byProject.map(({ project, threads }) => [project.id, threads] as const),
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex min-w-0 items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-[length:var(--app-font-size-ui,12px)] font-medium text-foreground hover:bg-accent/55">
        <ProjectAvatar project={props.focusedProject} size="md" />
        <span
          className="truncate text-[length:calc(var(--app-font-size-ui,12px)*1.2)]"
          style={{ color: resolveProjectColor(props.focusedProject) }}
        >
          {props.focusedProject.name}
        </span>
        <ChevronDownIcon className="size-3.5 shrink-0 text-muted-foreground/70" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-52">
        {props.projects.map((project) => {
          const attention = attentionByProjectId.get(project.id) ?? [];
          const isUrgent = attention.some(({ kind }) => kind !== "completed");
          return (
            <DropdownMenuItem key={project.id} onClick={() => props.onSelectProject(project)}>
              <ProjectAvatar project={project} size="sm" />
              <span className="min-w-0 flex-1 truncate text-[length:calc(var(--app-font-size-ui,12px)*1.2)]">
                {project.name}
              </span>
              {attention.length > 0 ? (
                <span
                  className={
                    isUrgent
                      ? "rounded-full bg-red-500/15 px-1.5 text-[10px] font-medium text-red-600 dark:text-red-300"
                      : "rounded-full bg-amber-500/15 px-1.5 text-[10px] font-medium text-amber-700 dark:text-amber-300"
                  }
                >
                  {attention.length}
                </span>
              ) : null}
            </DropdownMenuItem>
          );
        })}
        <div className="mx-2 my-1 h-px bg-border" />
        <DropdownMenuItem onClick={props.onShowAllProjects}>Todos los proyectos</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
